/** Hover context for derived values: formula, inputs used, how to improve. */
import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import type { CalculatedMetricExplanation } from '../../lib/metrics/calculatedMetricExplanation';

export function CalculatedMetricTooltipBody({ explanation }: { explanation: CalculatedMetricExplanation }) {
  return (
    <>
      <div className="font-medium text-text-primary mb-1">{explanation.label}</div>
      <div className="text-text-secondary">{explanation.formula}</div>
      <div className="mt-2 pt-2 border-t border-border-subtle">
        <div className="text-[10px] uppercase tracking-widest text-text-muted mb-0.5">Used</div>
        <div className="text-text-secondary">{explanation.inputs}</div>
      </div>
      <div className="mt-2 pt-2 border-t border-border-subtle">
        <div className="text-[10px] uppercase tracking-widest text-text-muted mb-0.5">To improve</div>
        <div className="text-text-secondary">{explanation.improve}</div>
      </div>
    </>
  );
}

export function CalculatedMetricHint({
  explanation, testId, iconClassName,
}: { explanation: CalculatedMetricExplanation; testId?: string; iconClassName?: string }) {
  const aria = `${explanation.formula} Used: ${explanation.inputs} To improve: ${explanation.improve}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} role="img" aria-label={aria}
          className={iconClassName ?? 'inline-flex items-center text-text-muted hover:text-text-primary cursor-help align-middle ml-0.5'}
          data-testid={testId ?? 'calculated-metric-tooltip'} onClick={e => e.stopPropagation()}>
          <Info className="w-3 h-3" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm text-xs whitespace-pre-line leading-relaxed"
        data-testid={testId ? `${testId}-content` : undefined}>
        <CalculatedMetricTooltipBody explanation={explanation} />
      </TooltipContent>
    </Tooltip>
  );
}

export function CalculatedMetricValue({
  explanation, children, testId, className,
}: { explanation: CalculatedMetricExplanation; children: React.ReactNode; testId?: string; className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className ?? 'cursor-help inline-block w-full'} data-testid={testId}
          onClick={e => e.stopPropagation()}>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm text-xs whitespace-pre-line leading-relaxed">
        <CalculatedMetricTooltipBody explanation={explanation} />
      </TooltipContent>
    </Tooltip>
  );
}

export function CalculatedMetricTooltip({
  explanation, testId, iconClassName,
}: { explanation: CalculatedMetricExplanation; testId?: string; iconClassName?: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <CalculatedMetricHint explanation={explanation} testId={testId} iconClassName={iconClassName} />
    </TooltipProvider>
  );
}
